import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
  accountVerificationGuard,
  AddressResolver,
  AuctionBidsResolver,
  AuthGuard,
  BalanceResolver,
  CardTokenResolver,
  CountryResolver,
  FantasyGameResolver,
  FantasyHistoryResolver,
  FGBPreLoadingStrategy,
  firstTimeUserGuard,
  LottoResolver,
  LottoWalletResolver,
  MaintenanceGuard,
  MemberCardResolver,
  // StepType,
  MemberResolver,
  PopulationGuard,
  PurchaseResolver,
  RegistrationStepResolver,
  StepType,
  SurveyResolver,
  TransactionSuccessGuard,
} from '@fgb/core';

// General
import { HomePageComponent } from './pages/home-page/home-page.component';
import { MasterPageComponent } from './pages/master/master-page/master-page.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';
// Loyalty
import { AuctionWalletDetailsPageComponent } from './pages/auction-wallet-details-page/auction-wallet-details-page.component';
import { BadgesPageComponent } from './pages/badges-page/badges-page.component';
import { EventsPageComponent } from './pages/events-page/events-page.component';
import { FantasyGamesPageComponent } from './pages/fantasy-games-page/fantasy-games-page.component';
import { GamesLayoutComponent } from './pages/games-layout/games-layout.component';
import { GamesPageComponent } from './pages/games-page/games-page.component';
import { LottoDetailPageComponent } from './pages/lotto-detail-page/lotto-detail-page.component';
import { LottoWalletDetailsPageComponent } from './pages/lotto-wallet-details-page/lotto-wallet-details-page.component';
import { LottosPageComponent } from './pages/lottos-page/lottos-page.component';
import { LottosSuccessPageComponent } from './pages/lottos-page/lottos-success-page/lottos-success-page.component';
import { MarketplaceDetailPageComponent } from './pages/marketplace-detail-page/marketplace-detail-page.component';
import { MarketplacePurchaseSuccessPageComponent } from './pages/marketplace-page/marketplace-purchase-success-page/marketplace-purchase-success-page.component';
import { PollsPageComponent } from './pages/polls-page/polls-page.component';
import { PredictorLayoutPageComponent } from './pages/predictor-page/predictor-layout-page/predictor-layout-page.component';
import { PredictorLeaderboardComponent } from './pages/predictor-page/predictor-leaderboard/predictor-leaderboard.component';
import { PredictorPageComponent } from './pages/predictor-page/predictor-page.component';
import { QuizPageComponent } from './pages/quiz-page/quiz-page.component';
import { RewardsLayoutComponent } from './pages/rewards-layout/rewards-layout.component';
import { WalletPageComponent } from './pages/wallet-page/wallet-page.component';

// Unauthenticated
import { AuthCallbackComponent } from './modules/user/components/auth/auth-callback/auth-callback.component';
import { SingleSignOnComponent } from './modules/user/components/single-sign-on/single-sign-on.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { MaintenancePageComponent } from './pages/maintenance-page/maintenance-page.component';
// Club Pages
import { ScorecardDescriptionComponent } from '@fgb/portal-component-library/src/lib/loyalty/scorecards';
import {
  provideRegistrationConfig,
  RegistrationPaymentCallbackPageComponent,
} from '@fgb/portal-component-library/src/lib/registration';
import { RegistrationAccountVerificationComponent } from 'src/app/modules/registration/pages/registration-account-verification/registration-account-verification.component';
import { RegistrationBenefitOptionsComponent } from 'src/app/modules/registration/pages/registration-benefits/registration-benefits-options.component';
import { RegistrationSuccessPageComponent } from 'src/app/modules/registration/pages/registration-success-page/registration-success-page.component';
import { RegistrationPaymentComponent } from 'src/app/modules/registration/pages/steps/registration-payment/registration-payment.component';
import { AccountVerifiedPageComponent } from 'src/app/pages/account-activation/account-verified-page/account-verified-page.component';
import { ActivateAccountPageComponent } from 'src/app/pages/account-activation/activate-account-page/activate-account-page.component';
import { FusionCallbackComponent } from './modules/club/components/fusion/fusion-callback/fusion-callback.component';
import { FantasyGamePlayComponent } from './modules/fantasy/components/fantasy-game-play/fantasy-game-play.component';
import { FantasyGameResultsComponent } from './modules/fantasy/components/fantasy-game-results/fantasy-game-results.component';
import { FantasyGameRulesComponent } from './modules/fantasy/components/fantasy-game-rules/fantasy-game-rules.component';
import { FantasyLeaderboardComponent } from './modules/fantasy/components/fantasy-leaderboard/fantasy-leaderboard.component';
import { FantasyUpNextComponent } from './modules/fantasy/components/fantasy-up-next/fantasy-up-next.component';
import { RegistrationLayoutPageComponent } from './modules/registration/pages/registration-layout-page/registration-layout-page.component';
import { RegistrationTermsAndConditionsComponent } from './modules/registration/pages/steps/registration-terms-and-conditions/registration-terms-and-conditions.component';
import { V1SingleSignOnComponent } from './modules/user/components/v1-single-sign-on/v1-single-sign-on.component';
import { ArcadePageComponent } from './pages/arcade-page/arcade-page.component';
import { ChallengesPageComponent } from './pages/challenges-page/challenges-page.component';
import { FantasyLeaderboardPageComponent } from './pages/fantasy-leaderboard-page/fantasy-leaderboard-page.component';
import { MarketplaceWalletPageComponent } from './pages/marketplace-wallet-page/marketplace-wallet-page.component';
import { QuizLayoutPageComponent } from './pages/quiz-page/quiz-layout-page/quiz-layout-page.component';
import { QuizLeaderboardComponent } from './pages/quiz-page/quiz-leaderboard/quiz-leaderboard.component';
import { RegistrationMarketingPageComponent } from './pages/registration-marketing-page/registration-marketing-page.component';
import { TrainingZonePageComponent } from './pages/training-zone-page/training-zone-page.component';
import { WalletClaimedPageComponent } from './pages/wallet-claimed-page/wallet-claimed-page.component';
import { WorksheetPageComponent } from './pages/worksheet-page/worksheet-page.component';
import { ProviderCallbackComponent } from './shared/components/providers/provider-callback/provider-callback.component';
import { Populations } from './shared/utilities/populations';
import { BrandPageComponent } from './pages/brand-page/brand-page.component';
import { BrandDetailPageComponent } from './pages/brand-page/brand-detail-page/brand-detail-page.component';

const routes: Routes = [
  {
    path: '',
    canActivate: [AuthGuard, firstTimeUserGuard(), accountVerificationGuard('/activate')],
    canActivateChild: [MaintenanceGuard],
    component: MasterPageComponent,
    resolve: {
      member: MemberResolver,
      memberCard: MemberCardResolver,
      balances: BalanceResolver,
    },
    children: [
      {
        path: '',
        canActivate: [PopulationGuard],
        data: {
          exclude: ['testPop'],
          popRedirect: 'test',
        },
        component: HomePageComponent,
      },

      {
        path: 'tv',
        component: WorksheetPageComponent,
      },
      {
        path: 'training-zone',
        component: TrainingZonePageComponent,
        canActivate: [PopulationGuard],
        data: {
          include: [Populations.Kids],
          popRedirect: '',
        },
      },
      {
        path: 'rewards',
        component: RewardsLayoutComponent,
        children: [
          {
            path: '',
            redirectTo: 'raffles',
            pathMatch: 'full',
          },

          {
            path: 'marketplace',
            redirectTo: 'events',
            pathMatch: 'full',
          },
          {
            path: 'marketplace',
            children: [
              {
                path: 'details/:id',
                children: [
                  {
                    path: '',
                    resolve: {
                      balances: BalanceResolver,
                      cardTokens: CardTokenResolver,
                      country: CountryResolver,
                    },
                    component: MarketplaceDetailPageComponent,
                  },
                ],
              },
              {
                path: 'purchase/success',
                canActivate: [TransactionSuccessGuard],
                component: MarketplacePurchaseSuccessPageComponent,
                resolve: {
                  balances: BalanceResolver,
                  purchase: PurchaseResolver,
                },
              },
            ],
          },
          {
            path: 'events',
            component: EventsPageComponent,
          },

          {
            path: 'raffles',
            children: [
              {
                path: '',
                component: LottosPageComponent,
                resolve: { lottos: LottoResolver, balances: BalanceResolver },
              },
              {
                path: 'details/:id',
                children: [
                  {
                    path: '',
                    resolve: { balances: BalanceResolver, lottos: LottoResolver },
                    component: LottoDetailPageComponent,
                  },
                ],
              },
              {
                path: 'purchase/success',
                canActivate: [TransactionSuccessGuard],
                component: LottosSuccessPageComponent,
              },
            ],
          },
          {
            path: 'wallet',
            children: [
              {
                path: '',
                component: WalletPageComponent,
              },
              {
                path: 'details/:id',
                component: MarketplaceWalletPageComponent,
                resolve: { purchases: PurchaseResolver },
              },
              {
                path: 'raffledetails/:id',
                component: LottoWalletDetailsPageComponent,
              },
            ],
          },
          {
            path: 'claimed',
            children: [
              {
                path: '',
                component: WalletClaimedPageComponent,
                resolve: {
                  purchases: PurchaseResolver,
                  lottoWalletItems: LottoWalletResolver,
                  auctionBids: AuctionBidsResolver,
                },
              },
            ],
          },
          {
            path: 'bids',
            children: [
              {
                path: 'details/:id',
                component: AuctionWalletDetailsPageComponent,
                resolve: { bids: AuctionBidsResolver },
              },
            ],
          },
        ],
      },

      {
        path: 'brand',
        canActivate: [PopulationGuard],
        data: {
          include: [Populations.Default, Populations.Club1901],
          popRedirect: '',
        },
        children: [
          { path: '', component: BrandPageComponent },
          { path: 'details/:id', component: BrandDetailPageComponent },
        ],
      },

      { path: 'badges', component: BadgesPageComponent },
      {
        path: 'challenges',
        component: ChallengesPageComponent,
      },
      { path: 'scorecard-description/:id', component: ScorecardDescriptionComponent },
      {
        path: 'games',
        resolve: {
          surveys: SurveyResolver,
          games: FantasyGameResolver,
        },
        component: GamesLayoutComponent,
        children: [
          { path: '', component: GamesPageComponent },
          {
            path: 'pickaplayer',
            component: FantasyGamesPageComponent,
            resolve: { games: FantasyGameResolver, history: FantasyHistoryResolver },
            children: [
              {
                path: '',
                redirectTo: 'play',
                pathMatch: 'full',
              },
              { path: 'play', component: FantasyGamePlayComponent },
              { path: 'leaderboard', component: FantasyLeaderboardComponent },
              { path: 'up-next', component: FantasyUpNextComponent },
              { path: 'rules', component: FantasyGameRulesComponent },
              { path: 'results', component: FantasyGameResultsComponent },
            ],
          },
          {
            path: 'predictor',
            component: PredictorLayoutPageComponent,
            children: [
              { path: '', component: PredictorPageComponent },
              { path: 'leaderboard', component: PredictorLeaderboardComponent },
            ],
          },
          { path: 'polls', component: PollsPageComponent },
          {
            path: 'trivia',
            component: QuizLayoutPageComponent,
            children: [
              { path: '', component: QuizPageComponent },
              { path: 'leaderboard', component: QuizLeaderboardComponent },
            ],
          },
          { path: 'arcade', component: ArcadePageComponent },
        ],
      },
      // Club Pages
      {
        path: 'fantasyleaderboard',
        component: FantasyLeaderboardPageComponent,
      },
      { path: '404page', component: NotFoundPageComponent },
      {
        path: 'provider/:providerid',
        component: ProviderCallbackComponent,
      },
    ],
  },

  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: 'login', component: LoginPageComponent, canActivate: [MaintenanceGuard] },
  {
    path: 'register',
    component: RegistrationLayoutPageComponent,
    loadChildren: () => import('@fgb/portal-component-library/src/lib/registration').then((m) => m.RegistrationModule),
    providers: [
      provideRegistrationConfig({
        routes: [
          {
            path: 'payment',
            children: [
              {
                path: '',
                component: RegistrationPaymentComponent,
                data: { registrationStep: StepType.Payment },
                resolve: { member: MemberResolver, address: AddressResolver },
              },
              {
                resolve: { member: MemberResolver, address: AddressResolver },
                path: 'result',
                component: RegistrationPaymentCallbackPageComponent,
              },
            ],
          },
          {
            path: 'terms',
            component: RegistrationTermsAndConditionsComponent,
            resolve: { registrationStep: RegistrationStepResolver },
            data: { registrationStep: StepType.TermsAndConditions },
          },
          {
            path: 'success',
            component: RegistrationSuccessPageComponent,
          },

          { path: 'welcome', component: RegistrationMarketingPageComponent, data: { registrationStep: StepType.Marketing } },
          {
            path: 'benefits',
            component: RegistrationBenefitOptionsComponent,
          },
          {
            path: 'verify',
            component: RegistrationAccountVerificationComponent,
          },
        ],
      }),
    ],
  },
  { path: 'activate-account', component: AccountVerifiedPageComponent },
  { path: 'activate', component: ActivateAccountPageComponent },
  { path: 'callback', component: AuthCallbackComponent },
  { path: 'sso', component: SingleSignOnComponent },
  { path: 'login/singleSignOn/:providerId', component: SingleSignOnComponent },
  { path: 'login/singleSignOn/:providerId/:clubId', component: SingleSignOnComponent },
  { path: 'login/ss/login.aspx', component: V1SingleSignOnComponent },
  { path: 'maintenance', component: MaintenancePageComponent },
  { path: 'fa-callback', component: FusionCallbackComponent },
  { path: '**', redirectTo: '404page' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      preloadingStrategy: FGBPreLoadingStrategy,
      bindToComponentInputs: true,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
